import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCustomActions,
  useDeleteCustomAction,
  useUpdateCustomActionStatus,
} from '../hooks';
import { CustomActionLogsModal } from '../components';
import { ToolCard } from '../components/ToolCard';
import { ToolListItem } from '../components/ToolListItem';
import { ALL_METHODS, type HttpMethod } from '../components/toolStyles';
import {
  FiltersButton,
  Button,
  FilterBottomSheet,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  SelectAllHeadCell,
  SortableHeadCell,
  ToolbarSearch,
  ViewToggle,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import type { CustomActionResponse, CustomActionQuery, ActiveStatus } from '@troia-v3/types';

type SortKey = 'name' | 'method' | 'status';

export const CustomActionsPage: React.FC = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();

  // "+" central do bottom-nav mobile → novo item (mockup .m-fab/.m-add).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); navigate('/automation/custom-actions/new'); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, [navigate]);
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<CustomActionQuery>({ page: 1, limit: 12 });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [methodFilter, setMethodFilter] = useState<HttpMethod | undefined>(undefined);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [logsAction, setLogsAction] = useState<CustomActionResponse | null>(null);

  const { data, isLoading, error } = useCustomActions(query);
  const deleteCustomAction = useDeleteCustomAction();
  const updateStatus = useUpdateCustomActionStatus();

  const items = useMemo(() => data?.data ?? [], [data]);

  // Sort client-side na página carregada (backend não expõe sort para ferramentas).
  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sort.key === 'name') cmp = a.displayName.localeCompare(b.displayName, 'pt-BR');
      else if (sort.key === 'method') cmp = a.endpoint.method.localeCompare(b.endpoint.method, 'pt-BR');
      else cmp = (a.status === 'active' ? 0 : 1) - (b.status === 'active' ? 0 : 1);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [items, sort]);

  // Filtro de Método é client-side (a query só suporta status/search server-side).
  const visibleItems = useMemo(
    () => (methodFilter ? sortedItems.filter((a) => a.endpoint.method === methodFilter) : sortedItems),
    [sortedItems, methodFilter],
  );

  const selectedCount = selectedIds.size;
  const allOnPageSelected = visibleItems.length > 0 && visibleItems.every((a) => selectedIds.has(a.id));
  const someOnPageSelected = visibleItems.some((a) => selectedIds.has(a.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ActiveStatus) => {
    setQuery((prev) => ({ ...prev, status, page: 1 }));
    setFiltersOpen(false);
  };

  const applyMethodFilter = (method?: HttpMethod) => {
    setMethodFilter(method);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, status: undefined, page: 1 }));
    setMethodFilter(undefined);
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) visibleItems.forEach((a) => next.delete(a.id));
      else visibleItems.forEach((a) => next.add(a.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const handleEdit = (id: string) => navigate(`/automation/custom-actions/${id}/edit`);

  const handleDelete = async (id: string) => {
    if (await confirm({ message: 'Tem certeza que deseja excluir esta ferramenta?', title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' })) {
      await deleteCustomAction.mutateAsync(id);
    }
  };

  const handleToggleStatus = async (action: CustomActionResponse) => {
    const newStatus = action.status === 'active' ? 'inactive' : 'active';
    await updateStatus.mutateAsync({ id: action.id, status: newStatus });
  };

  const handleViewLogs = (action: CustomActionResponse) => setLogsAction(action);

  const bulkDeactivate = () => {
    const targets = items.filter((a) => selectedIds.has(a.id) && a.status === 'active');
    targets.forEach((a) => updateStatus.mutate({ id: a.id, status: 'inactive' }));
    clearSelection();
  };

  const bulkDelete = async () => {
    if (!(await confirm({ message: `Excluir ${selectedCount} ferramenta(s) selecionada(s)?`, title: 'Confirmar exclusão', confirmButtonText: 'Excluir', variant: 'danger' }))) return;
    Array.from(selectedIds).forEach((id) => deleteCustomAction.mutate(id));
    clearSelection();
  };

  const statusLabel = query.status === 'active' ? 'Ativas' : query.status === 'inactive' ? 'Inativas' : null;
  const methodLabel = methodFilter ?? null;
  const activeFilterCount = [statusLabel, methodLabel].filter(Boolean).length;

  const pagination = data?.pagination;
  const total = pagination?.total;

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      {/* Status (server-side) */}
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todos'], ['active', 'Ativas'], ['inactive', 'Inativas']] as const).map(([val, label]) => {
        const active = query.status === val;
        return (
          <button
            key={label}
            onClick={() => setStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      {/* Método (client-side, sobre a página carregada) */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Método</p>
      <button
        onClick={() => applyMethodFilter(undefined)}
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
        style={{ color: '#374151', backgroundColor: !methodFilter ? '#f3e8ff' : 'transparent' }}
        onMouseEnter={(e) => { if (methodFilter) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
        onMouseLeave={(e) => { if (methodFilter) e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        Todos
        {!methodFilter && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
      </button>
      {ALL_METHODS.map((m) => {
        const active = methodFilter === m;
        return (
          <button
            key={m}
            onClick={() => applyMethodFilter(m)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            {m}
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          /* Esquerda: breadcrumb + contador */
          <>
          <nav className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate('/automation')} className="text-sm font-medium truncate hidden sm:inline" style={{ color: 'var(--theme-muted-text)' }}>
              Automação
            </button>
            <Icon name="chevron-right" className="w-3.5 h-3.5 hidden sm:inline" style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }} />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>Ferramentas</span>
          </nav>
          {total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-22) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar ferramenta…"
            shrinkClass="ds-search"
            data-testid="tools-search"
          />
        }
        right={
          /* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */
          <>
          {/* Filtros */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="tools-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="tools-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova ferramenta (único primário — F01-03) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={() => navigate('/automation/custom-actions/new')}
            data-testid="new-tool"
          >
            Nova ferramenta
          </Button>
          </>
        }
        mobile={
          /* `.m-sub` do mockup: busca + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar ferramenta…"
              data-testid="tools-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="tools-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {methodLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Método: {methodLabel}
              <button onClick={() => applyMethodFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de método">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="tools-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="tools-bulk-deactivate">
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="tools-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="tools-bulk-cancel">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}>
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar ferramentas</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando ferramentas..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message="Nenhuma ferramenta cadastrada"
              description={searchTerm || query.status ? 'Tente ajustar a busca ou os filtros.' : 'Crie sua primeira ferramenta para expandir as capacidades dos seus AI Agents'}
              actionLabel="Nova ferramenta"
              onAction={() => navigate('/automation/custom-actions/new')}
            />
          )}

          {!isLoading && !error && items.length > 0 && visibleItems.length === 0 && (
            <ListPageEmpty
              icon="filter"
              message="Nenhuma ferramenta com esse método"
              description="Tente ajustar o filtro de método."
              actionLabel="Limpar filtros"
              onAction={clearAllFilters}
              buttonVariant="secondary"
            />
          )}

          {/* Grid (Cards) — F34: sem checkbox de seleção em cards */}
          {!isLoading && !error && visibleItems.length > 0 && viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {visibleItems.map((action) => (
                <ToolCard
                  key={action.id}
                  action={action}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                  onViewLogs={handleViewLogs}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && visibleItems.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards (F34: sem checkbox) */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {visibleItems.map((action) => (
                  <ToolCard
                    key={action.id}
                    action={action}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                    onViewLogs={handleViewLogs}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse">
                  <thead>
                    {/* F43: head padrão shared (SortableHeadCell, densidade px-4 py-3) */}
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        data-testid="tools-select-all"
                      />
                      <SortableHeadCell label="Ferramenta" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} data-testid="tools-sort-name" />
                      <SortableHeadCell label="Método" sortKey="method" activeKey={sort.key} dir={sort.dir} onSort={handleSort} className="w-[110px]" data-testid="tools-sort-method" />
                      <PlainHeadCell label="Endpoint" />
                      <SortableHeadCell label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} className="w-[120px]" data-testid="tools-sort-status" />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.map((action) => (
                      <ToolListItem
                        key={action.id}
                        action={action}
                        selected={selectedIds.has(action.id)}
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                        onViewLogs={handleViewLogs}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação (F48 — padrão completo "Mostrando X–Y de N") */}
          {!isLoading && !error && pagination && pagination.total > 0 && (
            <ListPagePagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
              totalItems={pagination.total}
              pageSize={pagination.limit}
              itemLabel="ferramentas"
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="tools-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      {/* Logs Modal */}
      {logsAction && (
        <CustomActionLogsModal
          isOpen={!!logsAction}
          onClose={() => setLogsAction(null)}
          action={logsAction}
        />
      )}
    </div>
  );
};
